import React, { useEffect, useState, useContext } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { sortBy } from 'lodash';

import EnergyTracerTable from '../../components/tables';
import { linkFormatter } from '../../components/tables/formatters';
import EditBuildingModal from '../../components/modals/BuildingModals/EditBuildingModal';
import AddUtilityAccountModal from '../../components/modals/UtilityAccountModals/AddUtilityAccountModal';
import AddBuildingModal from '../../components/modals/BuildingModals/AddBuildingModal';
import EditOrganizationModal from '../../components/modals/OrganizationModals/EditOrganizationModal';
import { ApiServiceServerless } from '../../xhr_libs';
import { filterMaskedAccounts } from '../../helpers/resourceFilters';

import AppContext from '../../contexts/app-context';

const styles = {
  buttonDiv: { textAlign: 'right' },
  button: { marginRight: '0.5em', marginLeft: '0.5em' },
  buttonContainer: { textAlign: 'right', marginBottom: '1em' },
};

export default function OrganizationBuildingTable(props) {
  const {
    organization,
    updateAccess,
    deleteAccess,
    buildingCreateAccess,
    buildingUpdateAccess,
    accountCreateAccess,
    setOrganization,
    showToast,
    onDeleteClick,
  } = props;

  const history = useHistory();
  const { isAdmin } = useContext(AppContext);

  const [showEditOrgModal, setShowEditOrgModal] = useState(false);

  const [editBuildingId, setEditBuildingId] = useState(0);
  const [editBuilding, setEditBuilding] = useState({ id: 0 });
  const [showEditBuildingModal, setShowEditBuildingModal] = useState(false);

  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [utilityModalFlag, setUtilityModalFlag] = useState(false);
  const [defaultBuildings, setDefaultBuildings] = useState();

  const [showAddBuildingModal, setShowAddBuildingModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleEditBuildingSelect = (id) => {
    if (id === 0) {
      setEditBuilding({ id: 0 });
    } else {
      setIsLoading(true);
      ApiServiceServerless.get(`buildings/${id}`, {
        authorization_id: id,
      })
        .then((res) => {
          setEditBuilding({
            ...res.data,
          });
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          showToast('danger', 'Error', error);
          throw error;
        });
    }

    setEditBuildingId(id);
  };

  useEffect(() => {
    if (editBuildingId !== 0) {
      setShowEditBuildingModal(true);
    }
  }, [editBuildingId]);

  const handleSetUtilityModal = (id) => {
    const buildings = organization.buildings.filter(
      (building) => building.id === id
    );
    setDefaultBuildings(buildings.map((building) => building.id));
    setUtilityModalFlag(true);
  };

  useEffect(() => {
    if (utilityModalFlag) {
      setShowUtilityModal(true);
    }
  }, [utilityModalFlag]);

  const tableColumns = [
    {
      dataField: 'building.name',
      text: 'Building',
      formatter: linkFormatter((row) => [`/buildings/${row.id}`, row.name]),
    },
    {
      dataField: 'building.accounts',
      text: 'Utility Accounts',
      formatter: linkFormatter((row) => [
        `/buildings/${row.id}`,
        filterMaskedAccounts(row.accounts, isAdmin).length +
          ' utility accounts',
      ]),
    },
    {
      dataField: 'building.id',
      text: '',
      formatter: (cell, row) => (
        <div style={styles.buttonDiv}>
          <Button
            variant='edit'
            onClick={() => history.push(`/buildings/${row.id}`)}
            style={styles.button}
          >
            {' '}
            View{' '}
          </Button>
          {buildingUpdateAccess && (
            <Button
              variant='edit'
              onClick={() => handleEditBuildingSelect(row.id)}
              style={styles.button}
            >
              {' '}
              Edit{' '}
            </Button>
          )}
          {accountCreateAccess && (
            <Button
              variant='edit'
              onClick={() => handleSetUtilityModal(row.id)}
              style={styles.button}
            >
              {' '}
              Add Utility Account{' '}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleUpdateOrganization = (data) => {
    setOrganization((prev) => {
      let new_data = { ...prev };
      Object.keys(data).forEach((key) => {
        new_data[key] = data[key];
      });
      return new_data;
    });
  };

  const handleSetNewBuilding = (data) => {
    setOrganization((prev) => ({
      ...prev,
      buildings: [...prev.buildings, data],
    }));
  };

  const handleUpdateBuilding = (data) => {
    setOrganization((prev) => ({
      ...prev,
      buildings: prev.buildings.map((building) => {
        if (building['id'] !== data['id']) return building;
        return {
          ...data,
          siblings: building.siblings,
          accounts: building.accounts,
        };
      }),
    }));
  };

  const handleUpdateBuildingAccounts = (data) => {
    setOrganization((prev) => ({
      ...prev,
      buildings: prev.buildings.map((building) => {
        if (!data['building_ids'].includes(building['id'])) return building;
        let ret = { ...building };
        ret.accounts = [...ret.accounts, data];
        return ret;
      }),
    }));
  };

  return (
    <>
      <div style={styles.buttonContainer}>
        {updateAccess && (
          <Button
            variant='edit'
            style={styles.button}
            onClick={() => setShowEditOrgModal(true)}
          >
            Edit Organization
          </Button>
        )}
        {deleteAccess && (
          <Button
            variant='danger'
            style={styles.button}
            onClick={() => onDeleteClick()}
          >
            Delete Organization
          </Button>
        )}
        {buildingCreateAccess && (
          <Button
            variant='success'
            style={styles.button}
            onClick={() => {
              setShowAddBuildingModal(true);
            }}
          >
            New Building
          </Button>
        )}
      </div>
      <EnergyTracerTable
        keyField='id'
        columns={tableColumns}
        data={sortBy(organization.buildings, ['name'])}
      />
      <EditOrganizationModal
        show={showEditOrgModal}
        onHide={() => setShowEditOrgModal(false)}
        organization={organization}
        handleUpdateOrganization={handleUpdateOrganization}
        showToast={showToast}
      />
      <EditBuildingModal
        show={showEditBuildingModal}
        onHide={() => {
          setShowEditBuildingModal(false);
          handleEditBuildingSelect(0);
        }}
        building_id={editBuildingId}
        building={editBuilding}
        organization_name={organization.name}
        handleSetBuilding={handleUpdateBuilding}
        isLoading={isLoading}
        showToast={showToast}
      />
      <AddUtilityAccountModal
        organization={organization}
        buildings={organization.buildings}
        defaultBuildings={defaultBuildings}
        setDefaultBuildings={setDefaultBuildings}
        show={showUtilityModal}
        onHide={() => {
          setShowUtilityModal(false);
          setDefaultBuildings([]);
          setUtilityModalFlag(false);
        }}
        openAccountModal={() => setShowUtilityModal(true)}
        handleSetAccount={handleUpdateBuildingAccounts}
        showToast={showToast}
        history={history}
      />
      <AddBuildingModal
        organization={organization}
        organizations={[organization]}
        show={showAddBuildingModal}
        onHide={() => setShowAddBuildingModal(false)}
        openBuildingModal={() => {
          setShowAddBuildingModal(true);
        }}
        handleSetBuilding={handleSetNewBuilding}
        setDefaultBuildings={setDefaultBuildings}
        openAccountModal={() => setShowUtilityModal(true)}
        addAccountOption={true}
        history={history}
        showToast={showToast}
      />
    </>
  );
}
